import {
    Component,
    ElementRef,
    HostListener,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ViewChildren,
    QueryList,
    forwardRef,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR
} from '@angular/forms';
import { take } from 'rxjs/operators';
import { User, UserGroup } from '@intranet/modules/users/user.types';
import { UserService } from '@intranet/modules/users/user.service';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';

@Component({
    selector: 'user-picker',
    templateUrl: './user-picker.component.html',
    styleUrls: ['./user-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UserPickerComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserPickerComponent implements OnInit, OnChanges, ControlValueAccessor {
    @Input() placeholder = 'Select...';
    @Input() disabled = false;
    @Input() showAvatar = true;
    @Input() allowClear = true;
    @Input() currentUserId?: string;
    @Input() availableUsers?: User[];
    @Input() groups: UserGroup[] = [];
    // Optional: User object to display as selected, even if not in availableUsers (won't appear in dropdown)
    @Input() displayUser?: User;
    // Optional: Group name to display as selected, even if not in groups (won't appear in dropdown)
    @Input() displayGroupName?: string;

    @Output() userSelected = new EventEmitter<User | string | null>();

    @ViewChild('searchInput', { static: false }) searchInputRef!: ElementRef<HTMLInputElement>;
    @ViewChildren('option') optionRefs!: QueryList<ElementRef<HTMLElement>>;
    @Input() hideAll: boolean = false;
    @Input() hideTeams: boolean = false;
    @Input() minDropdownWidth: number = 0;
    @Input() multi: boolean = false;
    @Input() showApply: boolean = false;
    @Output() applySelection = new EventEmitter<string[]>();
    @Input() restrictedMode: boolean = false;
    @Input() emptyMessage: string = 'No owners available';
    // Multi + Apply only: make "All" a real select-all — while All is on,
    // every user's and team's box shows ticked too; unticking one leaves the
    // rest ticked; ticking everything turns back into All; clicking All while it's
    // on clears every box (Apply waits until something is ticked). The value
    // stays [] for All either way. Off by default, so existing multi pickers
    // (Timer reports) behave exactly as before.
    @Input() selectAllTicksAll: boolean = false;
    @ViewChild('trigger') trigger: CdkOverlayOrigin;


    users: User[] = [];
    activeUsers: User[] = [];
    inactiveUsers: User[] = [];

    searchQuery = '';
    selectedUser: User | string | null = null;
    dropdownOpen = false;
    highlightedIndex = -1;    // -1 = “All”
    optionsList: Array<{ type: 'all' | 'team' | 'user'; value: any }> = [];
    private searchInput$ = new Subject<string>();

    private onChange = (_: any) => {};
    private onTouched = () => {};
    private filterTimeout: any;
    dropUp = false;
    overlayWidth = 0;
    _multiValue: string[] = [];
    _pendingMultiValue: string[] | null = null;
    // selectAllTicksAll only: every box unticked in the open dropdown (as
    // opposed to [] = All). Apply is disabled while this is on.
    _pendingNone = false;
    selectedLabel = '';

    constructor(
        private userService: UserService,
        private eRef: ElementRef,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        // if the parent never passes in availableUsers, load the full list once
        // but skip this if restrictedMode is enabled (we only show what's explicitly provided)
        if (!this.availableUsers && !this.restrictedMode) {
            this.userService.users$
            .pipe(take(1))
            .subscribe(users => {
                this.users = users;
                this.filterUsers('');
                // auto-select current user if provided
                if (this.currentUserId) {
                    const me = users.find(u => u.id === this.currentUserId);
                    if (me) this.selectUser(me);
                }
            });
        } else if (this.restrictedMode) {
            // In restricted mode, use only what's provided (even if empty)
            this.users = this.availableUsers || [];
            this.filterUsers('');
        }

        this.searchInput$
          .pipe(debounceTime(200))
          .subscribe(q => {
              this.filterUsers(q);
              this.cdr.markForCheck();
          });
    }

    ngOnChanges(changes: SimpleChanges) {
        // if the availableUsers changed, update the users list
        if (changes.availableUsers && changes.availableUsers.currentValue !== changes.availableUsers.previousValue) {
            this.users = this.availableUsers || [];
            if (!this.dropdownOpen) {
                this.filterUsers(this.searchQuery);
            }
        }

        // if the groups changed, rebuild options
        if (changes.currentUserId
            && changes.currentUserId.currentValue !== changes.currentUserId.previousValue) {
              this.writeValue(changes.currentUserId.currentValue);
        }

        // Multi mode: a one-name label (user or team) can only be resolved
        // once those lists arrive — refresh it when they do.
        if (this.multi && (changes.availableUsers || changes.groups || changes.displayUser || changes.displayGroupName)) {
            this.updateSelectedLabel();
        }

        this.cdr.markForCheck();
    }


    trackByOption(_: number, opt: { type: 'all'|'team'|'user'; value: any }) {
        if (opt.type === 'all') return 'all';
        if (opt.type === 'team') return 'team-' + opt.value.userGroupId;
        return opt.value.id;
    }


    trackById(_: number, u: User) {
        return u.id;
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(e: Event) {
        const target = e.target as HTMLElement;
        if (this.eRef.nativeElement.contains(target)) return;
        // CDK overlay is rendered outside the host element — don't close on overlay clicks
        if (target.closest?.('.cdk-overlay-container')) return;
        this.closeDropdown();
    }

    closeDropdown() {
        if (this.multi && this.showApply) {
            this._pendingMultiValue = null;
            this._pendingNone = false;
        }
        this.dropdownOpen = false;
    }

    registerOnChange(fn: any) { this.onChange = fn; }
    registerOnTouched(fn: any) { this.onTouched = fn; }

    writeValue(value: string | string[] | null) {
        if (this.multi) {
            const arr = Array.isArray(value) ? value : (value ? [value] : []);
            this._multiValue = arr;
            this.updateSelectedLabel();
            this.cdr.markForCheck();
            return;
        }

        if (!value || Array.isArray(value)) {
            this.selectedUser = null;
            return;
        }

        // Handle team/group selection
        if ((value as string).startsWith?.('group-')) {
            this.selectedUser = value as string;
            return;
        }

        if (this.users.length === 0) {
            this.currentUserId = value as string;
        } else {
            const foundUser = this.users.find(u => u.id === value);
            if (foundUser) {
                this.selectedUser = foundUser;
            } else if (this.displayUser && this.displayUser.id === value) {
                this.selectedUser = this.displayUser;
            } else {
                this.selectedUser = null;
            }
        }
    }


    filterUsers(q: string) {
        // Normalize and split into tokens (preserve multi-word searches)
        this.searchQuery = q.trim().toLowerCase();
        const tokens = this.searchQuery.length
            ? this.searchQuery.split(/\s+/).filter(tok => tok.length > 0)
            : [];

        // helper: every token must match the start of at least one word in text
        const matchesPrefix = (text: string) => {
            const words = text.toLowerCase().split(/\s+/);
            return tokens.every(tok => words.some(w => w.startsWith(tok)));
        };

        const all = this.availableUsers ?? this.users;
        const filteredUsers = tokens.length
            ? all.filter(u => {
                  const label = u.fullName || `${u.firstName} ${u.surname}`;
                  return matchesPrefix(label);
              })
            : all.slice();

        // Sort helper for users
        const sortByName = (a: User, b: User) => {
            const nameA = (a.fullName || `${a.firstName} ${a.surname}`).toLowerCase();
            const nameB = (b.fullName || `${b.firstName} ${b.surname}`).toLowerCase();
            return nameA.localeCompare(nameB);
        };

        // Split and sort alphabetically
        this.activeUsers   = filteredUsers.filter(u => u.active).sort(sortByName);
        this.inactiveUsers = filteredUsers.filter(u => !u.active).sort(sortByName);

        // Sort teams alphabetically by group name
        const filteredGroups = tokens.length
            ? this.groups.filter(g => matchesPrefix(g.groupName))
            : this.groups.slice();
        filteredGroups.sort((a, b) =>
            a.groupName.toLowerCase().localeCompare(b.groupName.toLowerCase())
        );

        // Rebuild the flat options list: Active → Teams → Inactive
        this.optionsList = [];
        if (!this.hideAll) {
            this.optionsList.push({ type: 'all', value: null });
        }
        this.activeUsers.forEach(u => this.optionsList.push({ type: 'user', value: u }));
        if (!this.hideTeams) {
            filteredGroups.forEach(g => this.optionsList.push({ type: 'team', value: g }));
        }
        this.inactiveUsers.forEach(u => this.optionsList.push({ type: 'user', value: u }));

        // Highlight logic
        if (this.multi) {
            // Multi mode: nothing highlighted until the arrow keys are used.
            // A highlighted row previews a tick in its checkbox (see the
            // scss), so defaulting to "All" made All look ticked even when
            // specific users were selected.
            this.highlightedIndex = -1;
        } else if (!this.selectedUser) {
            this.highlightedIndex = 0;
        } else {
            this.highlightedIndex = this.optionsList.findIndex(opt => {
                if (opt.type === 'all') return this.selectedUser === null;
                if (opt.type === 'user' && typeof this.selectedUser !== 'string')
                    return opt.value.id === this.selectedUser.id;
                if (opt.type === 'team' && typeof this.selectedUser === 'string')
                    return +this.selectedUser.replace('group-', '') === opt.value.userGroupId;
                return false;
            });
            if (this.highlightedIndex < 0) this.highlightedIndex = 0;
        }

        this.scrollHighlightedIntoView();
    }


    onKeyDown(e: KeyboardEvent) {
        const total = this.optionsList.length;
        if (!this.dropdownOpen || total <= 0) return;

        if (e.key === 'ArrowDown') {
            this.highlightedIndex = (this.highlightedIndex + 1) % total;
            e.preventDefault();
            this.scrollHighlightedIntoView();
        } else if (e.key === 'ArrowUp') {
            // From "nothing highlighted" (multi mode's start), ↑ goes to the last row.
            this.highlightedIndex = this.highlightedIndex < 0
                ? total - 1
                : (this.highlightedIndex - 1 + total) % total;
            e.preventDefault();
            this.scrollHighlightedIntoView();
        } else if (e.key === 'Enter') {
            const item = this.optionsList[this.highlightedIndex];
            if (item) {
                if (item.type === 'all') this.clearUser();
                else if (item.type === 'team') this.selectUser('group-' + item.value.userGroupId);
                else if (item.type === 'user') this.selectUser(item.value);
            }
        }
    }

    toggleDropdown() {
        if (!this.dropdownOpen) {
            const ctrl = this.trigger.elementRef.nativeElement;
            const rect = ctrl.getBoundingClientRect();
            const triggerWidth = ctrl.offsetWidth;

            // Floor ensures longer labels (team names, full names) aren't clipped
            // even when the trigger itself is narrow.
            const desiredWidth = Math.max(triggerWidth, this.minDropdownWidth, 220);
            // Cap so the dropdown never overflows the viewport on small screens
            // (overlay is anchored to the trigger's left edge).
            const viewportCap = Math.max(triggerWidth, window.innerWidth - rect.left - 16);
            this.overlayWidth = Math.min(desiredWidth, viewportCap);

            const spaceBelow = window.innerHeight - rect.bottom;
            const maxHeightPx = window.innerHeight * 0.47;
            this.dropUp = spaceBelow < maxHeightPx;

            this.dropdownOpen = true;
            this.searchQuery = '';
            this.filterUsers('');

            if (this.multi && this.showApply) {
                this._pendingMultiValue = [...this._multiValue];
            } else {
                this._pendingMultiValue = null;
            }
            this._pendingNone = false;

            setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 0);
        } else {
            this.closeDropdown();
        }
    }

    selectUser(u: User | string | null) {
        if (this.multi && u && typeof u !== 'string') {
            this.toggleUserInMulti(u.id);
            return;
        }
        // Multi mode with teams shown (e.g. the Actions page's Owner filter):
        // a team is ticked/unticked alongside users, as "group-<id>", rather
        // than replacing the whole selection. Pickers that hide teams (the
        // Timer reports page) never get here.
        if (this.multi && typeof u === 'string' && u.startsWith('group-')) {
            this.toggleUserInMulti(u);
            return;
        }
        this.selectedUser = u;
        this.dropdownOpen = false;
        this.searchQuery = '';
        this.onChange(typeof u === 'string' ? u : u?.id ?? null);
        this.userSelected.emit(u);
    }

    clearUser() {
        // "All" clicked inside the open dropdown, as a select-all: toggles
        // between All (every box ticked) and none ticked, committed by Apply.
        // (The ✕ on the closed picker still clears and applies straight away,
        // below.)
        if (this.multi && this.showApply && this.selectAllTicksAll && this.dropdownOpen) {
            const isAll = !this._pendingNone && this.getMultiSelectionSource().length === 0;
            this._pendingMultiValue = [];
            this._pendingNone = isAll;
            this.cdr.markForCheck();
            return;
        }
        if (this.multi) {
            this._multiValue = [];
            this._pendingMultiValue = this.showApply ? [] : null;
            this.onChange(this._multiValue);
            if (this.showApply) {
                this.applySelection.emit([]);
            }
            this.updateSelectedLabel();
            this.cdr.markForCheck();
            return;
        }
        this.selectedUser = null;
        this.searchQuery = '';
        this.dropdownOpen = false;
        this.highlightedIndex = 0;
        this.onChange('');
        this.userSelected.emit('');
    }

    get selectedGroupName(): string | null {
        if (typeof this.selectedUser === 'string' && this.selectedUser.startsWith('group-')) {
            const groupId = parseInt(this.selectedUser.replace('group-', ''), 10);
            // First try to find in available groups, then fall back to displayGroupName
            const foundGroup = this.groups.find(g => g.userGroupId === groupId);
            if (foundGroup) {
                return foundGroup.groupName;
            }
            // Use displayGroupName if the group isn't in the available list
            return this.displayGroupName || null;
        }
        return null;
    }

    get showEmptyState(): boolean {
        // Show empty state only in restricted mode when there are no selectable options
        // (i.e., no users and either teams are hidden or no teams exist).
        // Counted before the search filters them — a search that matches no
        // one keeps the search box (see showNoMatches), so it can be retyped.
        if (!this.restrictedMode) return false;
        const hasUsers = (this.availableUsers ?? this.users).length > 0;
        const hasTeams = !this.hideTeams && this.groups.length > 0;
        return !hasUsers && !hasTeams;
    }

    // Restricted mode: there are people to pick, but the search matches none.
    get showNoMatches(): boolean {
        if (!this.restrictedMode || this.showEmptyState || !this.searchQuery) return false;
        return !this.optionsList.some((o) => o.type !== 'all');
    }

    highlightAndSelectUser(u: User, idx: number) {
        this.highlightedIndex = idx;
        this.selectUser(u);
    }

    // Scroll the highlighted option into view
    // This is necessary because the dropdown can be long and the highlighted option may not be visible
    private scrollHighlightedIntoView() {
        if (!this.optionRefs || this.optionRefs.length === 0) return;

        // If the highlighted index is -1, we want to scroll to the first item
        const listIndex = this.highlightedIndex + 1;
        const el = this.optionRefs.toArray()[listIndex];
        if (el) {
            el.nativeElement.scrollIntoView({ block: 'nearest' });
        }
    }

    getMultiSelectionSource(): string[] {
        if (this.multi && this.showApply && this.dropdownOpen && this._pendingMultiValue) {
            return this._pendingMultiValue;
        }
        return this._multiValue;
    }

    isUserOptionSelected(item: { type: string; value: any }): boolean {
        if (!this.multi) return false;
        // selectAllTicksAll: while All is on (an empty selection, not "none
        // ticked"), All and every user show ticked.
        const allOn = this.selectAllTicksAll
            && !(this.dropdownOpen && this._pendingNone)
            && this.getMultiSelectionSource().length === 0;
        if (item.type === 'all') {
            if (this.selectAllTicksAll) return allOn;
            const source = this.getMultiSelectionSource();
            return !source || source.length === 0;
        }
        if (item.type === 'user') {
            return allOn || this.getMultiSelectionSource().includes(item.value.id);
        }
        if (item.type === 'team') {
            return allOn || this.getMultiSelectionSource().includes('group-' + item.value.userGroupId);
        }
        return false;
    }

    private toggleUserInMulti(id: string) {
        if (this.showApply && this.selectAllTicksAll) {
            this.toggleWithSelectAll(id);
            return;
        }
        if (this.showApply) {
            const base = this._pendingMultiValue ? [...this._pendingMultiValue] : [...this._multiValue];
            const set = new Set(base);
            if (set.has(id)) { set.delete(id); } else { set.add(id); }
            this._pendingMultiValue = Array.from(set);
            this.cdr.markForCheck();
            return;
        }
        const set = new Set(this._multiValue);
        if (set.has(id)) { set.delete(id); } else { set.add(id); }
        this._multiValue = Array.from(set);
        this.onChange(this._multiValue);
        this.updateSelectedLabel();
        this.cdr.markForCheck();
    }

    // selectAllTicksAll's toggle. All ticks every user AND every team (All
    // means no filter, which includes team-owned actions too). From All,
    // unticking one (user or team) means "everything else"; ticking every
    // user and team back means All again; unticking the last one means none
    // ticked (not All).
    private toggleWithSelectAll(id: string) {
        const source = this.getMultiSelectionSource();
        const allUserIds = (this.availableUsers ?? this.users).map(u => u.id);
        const allTeamIds = this.hideTeams ? [] : this.groups.map(g => 'group-' + g.userGroupId);
        const everything = [...allUserIds, ...allTeamIds];
        let set: Set<string>;
        if (!this._pendingNone && source.length === 0) {
            // From All: untick just the one clicked.
            set = new Set(everything.filter(v => v !== id));
        } else {
            set = new Set(source);
            if (set.has(id)) { set.delete(id); } else { set.add(id); }
        }
        const picked = Array.from(set);
        const everyOne = everything.length > 0
            && picked.length === everything.length
            && everything.every(v => set.has(v));
        if (everyOne) {
            this._pendingMultiValue = [];
            this._pendingNone = false;
        } else {
            this._pendingMultiValue = picked;
            this._pendingNone = picked.length === 0;
        }
        this.cdr.markForCheck();
    }

    onApplyClick() {
        // Nothing ticked isn't a filter — Apply is disabled for it.
        if (this._pendingNone) return;
        const committed = this._pendingMultiValue ? [...this._pendingMultiValue] : [];
        this._multiValue = committed;
        this.onChange(this._multiValue);
        this.updateSelectedLabel();
        this.applySelection.emit(this._multiValue);
        this.dropdownOpen = false;
        this._pendingMultiValue = null;
    }

    private updateSelectedLabel() {
        if (!this.multi) return;
        if (!this._multiValue || this._multiValue.length === 0) {
            this.selectedLabel = '';
            return;
        }
        // Teams ("group-<id>") can be in the selection too, when teams are
        // shown — see selectUser.
        const teamIds = this._multiValue.filter(v => v.startsWith('group-'));
        const userIds = this._multiValue.filter(v => !v.startsWith('group-'));
        if (this._multiValue.length === 1) {
            if (teamIds.length) {
                const groupId = +teamIds[0].replace('group-', '');
                const group = this.groups.find(g => g.userGroupId === groupId);
                this.selectedLabel = group ? group.groupName : (this.displayGroupName || '1 team selected');
                return;
            }
            const user = (this.availableUsers ?? this.users).find(u => u.id === userIds[0])
                ?? (this.displayUser?.id === userIds[0] ? this.displayUser : undefined);
            this.selectedLabel = user ? (user.fullName || `${user.firstName} ${user.surname}`) : '1 user selected';
            return;
        }
        if (!teamIds.length) {
            this.selectedLabel = `${userIds.length} users selected`;
            return;
        }
        if (!userIds.length) {
            this.selectedLabel = `${teamIds.length} teams selected`;
            return;
        }
        const users = userIds.length === 1 ? '1 user' : `${userIds.length} users`;
        const teams = teamIds.length === 1 ? '1 team' : `${teamIds.length} teams`;
        this.selectedLabel = `${users}, ${teams} selected`;
    }
}
